/**
 * AboutPage.Mockups.tsx
 * Purely illustrative, decorative mockup components for the public About page.
 * These components are self-contained (TSX + ab-mock-* CSS classes only, no new npm deps)
 * and contain no real data — all names, addresses, and figures are entirely fictional.
 * They exist solely to give visitors a tangible visual impression of each feature.
 */

/* ── InboxMockup ─────────────────────────────────────────────────────────── */

interface InboxRow {
  from: string;
  subject: string;
  tags: string[];
  time: string;
  read: boolean;
  /** Show a replied arrow icon to indicate the user has sent a reply. */
  replied?: boolean;
}

interface InboxMockupProps {
  rows: InboxRow[];
  /** Optional tag name to give the accent outline pill style. */
  highlight?: string;
}

// Colours for the core Action status categories match the auto-assigned
// Outlook preset colours (see src/lib/categorisation/outlook-colours.ts).
const ACTION_CATEGORY_COLOURS: Record<string, string> = {
  'to respond':     'red',
  'awaiting reply': 'blue',
  'actioned':       'green',
  'for info':       'orange',
  'notification':   'purple',
  'marketing':      'teal',
};

function pillColour(tag: string): string {
  // Only the core Action status categories get a colour; all custom/topic
  // categories are deliberately grey in these mockups.
  return ACTION_CATEGORY_COLOURS[tag.toLowerCase()] ?? 'slate';
}

function isAction(tag: string): boolean {
  return Object.keys(ACTION_CATEGORY_COLOURS).includes(tag.toLowerCase());
}

export function InboxMockup({ rows, highlight }: InboxMockupProps) {
  return (
    <div className="ab-mock-wrap">
      {rows.map((row, i) => (
        <div key={i} className={`ab-mock-row${row.read ? '' : ' ab-mock-row-unread'}`}>
          <span
            className={`ab-mock-dot${row.read ? ' ab-mock-dot--read' : ''}`}
            aria-hidden="true"
          />
          <div className="ab-mock-meta">
            <span className="ab-mock-sender">{row.from}</span>
            <span className="ab-mock-subject">              
              {row.subject}
              {row.replied && <span className="ab-mock-replied-icon" aria-label="Replied" title="Replied">&#8617;</span>}
            </span>
          </div>
          <div className="ab-mock-right">
            <span className="ab-mock-time">{row.time}</span>
            <div className="ab-mock-tags">
              {row.tags.map((tag) => (
                <span
                  key={tag}
                  className={[
                    'ab-mock-pill',
                    `ab-mock-pill-${pillColour(tag)}`,
                    ((highlight && tag === highlight) || isAction(tag)) ? 'ab-mock-pill--highlight' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── SLAMockup ───────────────────────────────────────────────────────────── */

export function SLAMockup() {
  return (
    <div className="ab-mock-wrap">
      <div className="ab-mock-stat-wrap">
        <div className="ab-mock-stat ab-mock-stat-red">
          <div className="ab-mock-stat-count">2</div>
          <div className="ab-mock-stat-label">Overdue</div>
        </div>
        <div className="ab-mock-stat ab-mock-stat-amber">
          <div className="ab-mock-stat-count">4</div>
          <div className="ab-mock-stat-label">Due today</div>
        </div>
        <div className="ab-mock-stat ab-mock-stat-green">
          <div className="ab-mock-stat-count">17</div>
          <div className="ab-mock-stat-label">On track</div>
        </div>
      </div>
    </div>
  );
}

/* ── DigestMockup ────────────────────────────────────────────────────────── */

export function DigestMockup() {
  return (
    <div className="ab-mock-wrap">
      <div className="ab-mock-digest-heading">Outstanding actions — 3 threads</div>
      <div className="ab-mock-digest-row">
        <div className="ab-mock-digest-meta">
          <div className="ab-mock-digest-from">Alice Chen</div>
          <div className="ab-mock-digest-subject">Q2 budget sign-off</div>
        </div>
        <span className="ab-mock-digest-badge ab-mock-digest-badge-amber">Awaiting reply</span>
      </div>
      <div className="ab-mock-digest-row">
        <div className="ab-mock-digest-meta">
          <div className="ab-mock-digest-from">Bob at PartnerCo</div>
          <div className="ab-mock-digest-subject">Partnership proposal</div>
        </div>
        <span className="ab-mock-digest-badge ab-mock-digest-badge-red">Overdue</span>
      </div>
    </div>
  );
}



/* ── RulesMockup ────────────────────────────────────────────────────────── */

interface RuleCondition {
  field: string;
  op: string;
  value: string;
}

interface RuleRow {
  conditions: RuleCondition[];
  action: string;
}

interface RulesMockupProps {
  rules: RuleRow[];
}

export function RulesMockup({ rules }: RulesMockupProps) {
  return (
    <div className="ab-mock-wrap">
      {rules.map((rule, i) => (
        <div key={i} className="ab-mock-rule">
          <div className="ab-mock-rule-if-row">
            <span className="ab-mock-rule-label-if">if</span>
            {rule.conditions.map((cond, j) => (
              <>
                {j > 0 && <span className="ab-mock-rule-and">and</span>}
                <span key={j} className="ab-mock-rule-cond">
                  <span className="ab-mock-rule-cond-field">{cond.field}</span>
                  <span className="ab-mock-rule-cond-op">{cond.op}</span>
                  <span className="ab-mock-rule-cond-val">{cond.value}</span>
                </span>
              </>
            ))}
          </div>
          <div className="ab-mock-rule-then-row">
            <span className="ab-mock-rule-label-then">&#8594;</span>
            <span className="ab-mock-rule-action">{rule.action}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
/* ── SLAConfigMockup ────────────────────────────────────────────────────── */

interface SLAWindow {
  condition: string;
  deadline: string;
  colour: 'green' | 'amber';
}

interface SLAConfigMockupProps {
  client: string;
  windows: SLAWindow[];
}

export function SLAConfigMockup({ client, windows }: SLAConfigMockupProps) {
  return (
    <div className="ab-mock-wrap">
      <div className="ab-mock-sla-header">
        <span className="ab-mock-sla-client">{client}</span>
        <span className="ab-mock-sla-tag">SLA active</span>
      </div>
      {windows.map((w, i) => (
        <div key={i} className="ab-mock-sla-row">
          <span className="ab-mock-sla-cond">{w.condition}</span>
          <span className={`ab-mock-sla-deadline ab-mock-sla-deadline-${w.colour}`}>
            {w.deadline}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── SuggestionChips ─────────────────────────────────────────────────────── */
export function SuggestionChips() {
  const chips = [
    'Add category: Finance',
    'Add rule: move read invoices to Finance folder',
    'Add category: Ops alerts',
    'Add rule: Archive `Ops alerts` after 7 days',
  ];
  return (
    <div className="ab-mock-wrap">
      <div className="ab-mock-chip-list">
        {chips.map((chip) => (
          <span key={chip} className="ab-mock-chip">
            <span aria-hidden="true">+</span>
            {chip}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── LitterTrayMockup ────────────────────────────────────────────────────── */

type LitterAction = 'unsubscribe' | 'file' | 'keep';

interface LitterRow {
  from: string;
  detail: string;
  action: LitterAction;
  /** Target folder shown for `file` suggestions. */
  folder?: string;
}

const LITTER_ACTION_LABEL: Record<LitterAction, string> = {
  unsubscribe: 'Unsubscribe',
  file: 'File',
  keep: 'Keep',
};

export function LitterTrayMockup() {
  const rows: LitterRow[] = [
    { from: 'Daily Deals Club', detail: '48 emails · 4% opened', action: 'unsubscribe' },
    { from: 'Fieldside Garden Centre', detail: '22 emails · 9% opened', action: 'unsubscribe' },
    { from: 'ShopHub orders', detail: '17 receipts & dispatch notes', action: 'file', folder: 'Receipts' },
    { from: 'Northline Rail', detail: '9 e-tickets & journey updates', action: 'file', folder: 'Travel' },
    { from: 'Village Hall Committee', detail: '12 emails · 83% opened', action: 'keep' },
  ];
  return (
    <div className="ab-mock-wrap ab-mock-lt">
      <div className="ab-mock-digest-heading ab-mock-lt-heading">
        <span>Clean-up report</span>
        <span className="ab-mock-lt-range">Last 6 months</span>
      </div>
      <div className="ab-mock-stat-wrap ab-mock-lt-stats">
        <div className="ab-mock-stat ab-mock-stat-red">
          <div className="ab-mock-stat-count">31</div>
          <div className="ab-mock-stat-label">to unsubscribe</div>
        </div>
        <div className="ab-mock-stat ab-mock-stat-amber">
          <div className="ab-mock-stat-count">1,240</div>
          <div className="ab-mock-stat-label">emails to file</div>
        </div>
        <div className="ab-mock-stat ab-mock-stat-green">
          <div className="ab-mock-stat-count">68</div>
          <div className="ab-mock-stat-label">senders kept</div>
        </div>
      </div>
      {rows.map((row) => (
        <div key={row.from} className="ab-mock-digest-row">
          <div className="ab-mock-digest-meta">
            <div className="ab-mock-digest-from">{row.from}</div>
            <div className="ab-mock-digest-subject">{row.detail}</div>
          </div>
          <span className={`ab-mock-digest-badge ab-mock-lt-badge-${row.action}`}>
            {LITTER_ACTION_LABEL[row.action]}
            {row.folder && <> &#8594; {row.folder}</>}
          </span>
        </div>
      ))}
    </div>
  );
}
